import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Fab,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  GlobalStyles,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  LightbulbOutlined,
  AttachMoney,
  Computer,
  LocationOn,
  Assignment,
  Home,
  CheckCircle,
  Star,
  TrendingUp,
  Group,
  Favorite,
  EmojiEvents,
  School,
  Public,
  CalendarToday,
  ExpandMore,
  Cancel,
} from '@mui/icons-material';
import CountdownTimer from './components/CountdownTimer';
import BookingWidget from './BookingWidget';
import CorporateExitRoadmap from './components/CorporateExitRoadmap';
import DreamBand from './components/DreamBand';
import { CTA_LABEL, CTA_SUBLINE, COHORT_START, COHORT_START_TS } from './config';

// Avatar row beside the member count. The previous version generated ids
// arithmetically from 1500000000000, none of which exist, so all ten rendered as
// grey silhouettes. Every id below was requested and confirmed to return 200
// before being put here.
const MEMBER_AVATARS = [
  'photo-1506863530036-1efeddceb993',
  'photo-1587397845856-e6cf49176c70',
  'photo-1509460913899-515f1df34fea',
  'photo-1533933269825-da140ad3132f',
  'photo-1674932668403-33398b81c92f',
  'photo-1600603406200-5b2a104684ac',
  'photo-1581841064838-a470c740e8ee',
  'photo-1583674392771-2abf6be75965',
  'photo-1518611540400-6b85a0704342',
  'photo-1574526783053-c3afac70d448',
];

// The keyframes the page animates against. These used to live in a `<style jsx>`
// tag, which is styled-jsx syntax that this project does not use: React rendered
// it as <style jsx="true"> and logged an error on every page load, and the pulse
// rule was written with camelCase `boxShadow` inside a CSS string, so it had zero
// valid declarations. GlobalStyles injects them properly.
const keyframes = (
  <GlobalStyles
    styles={{
      '@keyframes pulse': {
        '0%, 100%': { boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px rgba(16, 185, 129, 0.3)' },
        '50%': { boxShadow: '0 25px 50px rgba(16, 185, 129, 0.2), 0 0 0 4px rgba(16, 185, 129, 0.5)' },
      },
      '@keyframes blink': {
        '0%, 50%, 100%': { opacity: 1 },
        '25%, 75%': { opacity: 0.5 },
      },
      '@keyframes shimmer': {
        '0%, 100%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
      },
      // Anchored sections must clear the fixed header when scrolled to.
      '#framework, #curriculum, #florian, #faq, #enroll': { scrollMarginTop: '80px' },
      '@keyframes textGlow': {
        '0%': { filter: 'drop-shadow(0 0 5px rgba(200, 121, 65, 0.3))' },
        '100%': { filter: 'drop-shadow(0 0 20px rgba(200, 121, 65, 0.6))' },
      },
      // float, scaleIn, slideInLeft and slideInRight were referenced by sx but
      // defined only in App.css, which nothing imports. Defined here so the
      // declarations that call them are no longer dead.
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-12px)' },
      },
      '@keyframes scaleIn': {
        from: { opacity: 0, transform: 'scale(0.96)' },
        to: { opacity: 1, transform: 'scale(1)' },
      },
      '@keyframes slideInLeft': {
        from: { opacity: 0, transform: 'translateX(-24px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
      '@keyframes slideInRight': {
        from: { opacity: 0, transform: 'translateX(24px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
    }}
  />
);

function App() {
  // Calculator state
  const [calculatorInput, setCalculatorInput] = useState({
    salary: '',
    savings: '',
    location: 'Thailand'
  });
  const [calculationResults, setCalculationResults] = useState(null);
  const [showRoadmap, setShowRoadmap] = useState(false);

  // Scroll progress state
  const [progress, setProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Hide the floating CTA once the booking widget is on screen, or it sits on
  // top of the 650px Calendly iframe it is trying to send people to.
  const [enrollInView, setEnrollInView] = useState(false);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setProgress(Math.min(scrollPercent, 100));
      setShowScrollTop(scrollTop > 400);
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  useEffect(() => {
    const enroll = document.getElementById('enroll');
    if (!enroll || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setEnrollInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(enroll);
    return () => observer.disconnect();
  }, [showRoadmap]);

  const scrollToEnroll = () => {
    document.getElementById('enroll')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Cost of living data for different locations
  const costOfLivingData = {
    'Thailand': 1200,
    'Portugal': 1800,
    'Mexico': 1100,
    'Malaysia': 900,
    'Costa Rica': 1300,
    'Spain': 1600,
    'Indonesia': 800,
    'Philippines': 850,
    'Vietnam': 700,
    'Ecuador': 600
  };

  // Calculator calculation function
  const calculateResults = useCallback(() => {
    const salary = parseFloat(calculatorInput.salary) || 0;
    const savings = parseFloat(calculatorInput.savings) || 0;
    const monthlyCostOfLiving = costOfLivingData[calculatorInput.location] || 1200;

    // Six months of runway priced in the TARGET country, not the current one.
    const runwayNeeded = monthlyCostOfLiving * 6;

    const formatDate = (d) =>
      d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Only calculate if we have valid inputs
    if (salary <= 0 && savings <= 0) return null;

    // No salary means no saving rate, so there is no honest date to give. This
    // used to return a hardcoded 'December 1, 2025', which went stale and told
    // visitors their exit date was in the past.
    if (salary <= 0) {
      return {
        exitDate: 'Add your salary to get a date',
        monthlyRunwayNeeded: `$${runwayNeeded.toLocaleString()}`,
        timeframe: savings >= runwayNeeded
          ? 'Runway is covered, income is your only gap'
          : 'Keep saving, and start building income now'
      };
    }

    const remainingToSave = Math.max(0, runwayNeeded - savings);

    // Module 2 targets saving 30-50% of corporate income. Use the midpoint:
    // assuming someone banks 100% of gross salary produces exit dates nobody
    // can actually hit.
    const monthlySavingRate = (salary / 12) * 0.4;

    // Runway already covered: the gap is income, not savings.
    if (remainingToSave <= 0) {
      const exitDate = new Date();
      exitDate.setDate(exitDate.getDate() + 30); // Give them a month to plan
      return {
        exitDate: formatDate(exitDate),
        monthlyRunwayNeeded: `$${runwayNeeded.toLocaleString()}`,
        timeframe: 'Runway covered, 30 days to plan'
      };
    }

    const monthsToSave = Math.ceil(remainingToSave / monthlySavingRate);

    // Past ten years, a date is a fiction, so say so instead of printing one.
    if (monthsToSave > 120) {
      return {
        exitDate: "Saving alone won't get you there",
        monthlyRunwayNeeded: `$${runwayNeeded.toLocaleString()}`,
        timeframe: 'This is exactly why the income half exists'
      };
    }

    const exitDate = new Date();
    exitDate.setMonth(exitDate.getMonth() + monthsToSave);

    return {
      exitDate: formatDate(exitDate),
      monthlyRunwayNeeded: `$${runwayNeeded.toLocaleString()}`,
      timeframe: `${monthsToSave} months`
    };
  }, [calculatorInput]);

  // Calculate results when input changes - but only show when button clicked
  // Note: Removed automatic calculation - now only triggered by button click

  // If showing roadmap, return only the roadmap component. onBack is what makes
  // this survivable. Without it the visitor is stranded with no route back.
  if (showRoadmap) {
    return <CorporateExitRoadmap onBack={() => setShowRoadmap(false)} />;
  }

  return (
    <Box>
      {keyframes}

      {/* Sticky header. The page previously had no nav of any kind and exactly
          one anchor, so a visitor 12 screens down had no way back to the offer. */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          // A fixed header still sizes to its content unless it is clamped, so
          // long labels at large text pushed the whole bar past the viewport.
          maxWidth: '100vw',
          overflowX: 'hidden',
          bgcolor: showScrollTop ? 'rgba(250, 250, 247, 0.85)' : 'transparent',
          backdropFilter: showScrollTop ? 'blur(12px)' : 'none',
          borderBottom: '1px solid',
          borderColor: showScrollTop ? 'divider' : 'transparent',
          transition: 'background-color 250ms, border-color 250ms',
        }}
      >
        <Toolbar disableGutters sx={{ maxWidth: 1200, width: '100%', mx: 'auto', px: { xs: 1.5, sm: 2.5, md: 3 }, gap: { xs: 1, sm: 2, md: 3 }, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}
          >
            <Avatar src="/img/the-freedome-framework-logo.png" alt="" sx={{ width: 32, height: 32, flexShrink: 0 }} />
            <Typography
              variant="h6"
              noWrap
              sx={{
                color: showScrollTop ? 'text.primary' : 'common.white',
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              The Freedom Framework
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2.5} sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
            {[
              { label: 'Framework', id: 'framework' },
              { label: 'Curriculum', id: 'curriculum' },
              { label: 'Florian', id: 'florian' },
              { label: 'FAQ', id: 'faq' },
            ].map((link) => (
              <Button
                key={link.id}
                onClick={() => document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ px: 1, py: 0.5, color: showScrollTop ? 'text.secondary' : 'rgba(255,255,255,0.85)' }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            onClick={scrollToEnroll}
            sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.875rem', sm: '1rem' }, flexShrink: 0 }}
          >
            {/* The full label cannot shrink, so it is the thing that pushes the
                header past the viewport on a narrow screen at large text. */}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Book a call</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Book</Box>
          </Button>
        </Toolbar>
      </AppBar>

      {/* Scroll Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: 'background.paper',
          zIndex: 1100,
          '& .MuiLinearProgress-bar': {
            bgcolor: 'secondary.main',
          },
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f1f3d 100%)',
          color: 'white',
          minHeight: { xs: 'auto', md: '88vh' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          // Top padding clears the fixed header, which floats over this section.
          pt: { xs: 10, md: 14 },
          pb: { xs: 6, md: 8 },
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'url(/img/averie-woodard-5d20kdvFCfA-unsplash.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center 60%',
            },
            // Scrim over the photograph rather than a veil across it. The image
            // carries the promise, so it stays visible; this only buys back the
            // contrast the headline needs, and only where the headline sits.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(180deg, rgba(15,31,61,0.92) 0%, rgba(15,31,61,0.78) 38%, rgba(15,31,61,0.62) 70%, rgba(15,31,61,0.80) 100%)',
            },
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={{ xs: 2.5, md: 4 }} alignItems="center" textAlign="center">
            {/* Badge */}
            <Typography variant="overline" sx={{ color: 'secondary.light', display: 'block', mb: 1.5 }}>
              FOR CORPORATE EMPLOYEES WHO WANT OUT, WITHOUT LOSING THE INCOME
            </Typography>

            {/* Hero Title */}
            <Typography
              variant="h1"
              sx={{
                // min() rather than a bare 900 so the heading can never be
                // wider than the space it actually has.
                maxWidth: 'min(900px, 100%)',
                overflowWrap: 'break-word',
              }}
            >
              You Don't Have to Quit to Start Leaving. <br />
              <Box component="span" sx={{ color: '#3dbaa8' }}>
                Replace Your Corporate Income Before You Hand In Your Notice.
              </Box>
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="h6"
              sx={{
                maxWidth: 700,
                opacity: 0.9,
              }}
            >
              You've got the salary, the title, and a Sunday-night knot in your stomach. I'm Florian.
              I left the same setup three years ago. On a free 30-minute call we'll map your exit date,
              your income number, and where month one starts.
            </Typography>

            {/* Stats */}
            <Grid container spacing={{ xs: 1, sm: 4 }} justifyContent="center" sx={{ my: { xs: 1.5, sm: 4 } }}>
              <Grid size={{ xs: 4 }}>
                <Stack alignItems="center">
                  <Typography variant="h3" sx={{ color: '#3dbaa8', fontWeight: 800, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
                    500+
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.9375rem' }, lineHeight: 1.35 }}>
                    Students Transformed
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Stack alignItems="center">
                  <Typography variant="h3" sx={{ color: '#3dbaa8', fontWeight: 800, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
                    6 months
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.9375rem' }, lineHeight: 1.35 }}>
                    First client to last day at work
                  </Typography>
                </Stack>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Stack alignItems="center">
                  <Typography variant="h3" sx={{ color: '#3dbaa8', fontWeight: 800, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
                    10
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.9375rem' }, lineHeight: 1.35 }}>
                    Seats in the {COHORT_START} cohort
                  </Typography>
                </Stack>
              </Grid>
            </Grid>

            {/* CTA Button */}
            <Button
              variant="contained"
              color="secondary"
              size="large"
              href="#enroll"
              sx={{
                mt: 2,
                px: 6,
                py: 2,
                fontSize: '1.125rem',
              }}
            >
              {CTA_LABEL}
            </Button>

            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1, maxWidth: 620 }}>
              {CTA_SUBLINE}
            </Typography>

            {/* Countdown Timer */}
            <CountdownTimer targetDate={COHORT_START_TS} compact={false} />
          </Stack>
        </Container>
      </Box>

      {/* Video Section. Sits directly under the hero: the research on VSL pages is
          consistent that the video belongs high, supporting the headline rather
          than buried behind narrative sections. The headline still carries the
          offer on its own if the video never loads. */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 5, md: 7 } }}>
        <Container>
          <Typography variant="h4" textAlign="center" sx={{ fontWeight: 800, mb: 1 }}>
            Watch This First (3:51)
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 4, maxWidth: 720, mx: 'auto', alignSelf: 'center' }}>
            Florian on what the six months actually looked like, while he was still employed,
            still paying rent, and still had a manager to answer to.
          </Typography>
          <Box
            component="video"
            controls
            preload="metadata"
            poster="/img/vsl-poster.jpg"
            sx={{
              width: '100%',
              maxWidth: 960,
              height: 'auto',
              borderRadius: 3,
              mx: 'auto',
              display: 'block',
            }}
          >
            <source src="/6de0f7760d.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={scrollToEnroll}
              sx={{ fontWeight: 700 }}
            >
              {CTA_LABEL} →
            </Button>
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              {CTA_SUBLINE}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* About Florian Section */}
      <Box id="florian" sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" gutterBottom>
            Meet Florian: From Corporate Prison to Freedom Architect
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 8 }}>
            Three years ago, I was the poster child for 'doing everything right' but dying inside. Today, I'm living proof that building a life of genuine freedom isn't just possible, it's achievable with the right framework.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 8 }}>
            {[
              { number: '$10K', label: 'First month profit', description: 'AI automation systems' },
              { number: '$50K', label: 'Photography business', description: 'One year revenue' },
              { number: '200+', label: 'Property units', description: 'Scaled in 24 months' },
              { number: '20+', label: 'Countries lived', description: 'True location freedom' },
            ].map((achievement) => (
              <Grid size={{ xs: 6, md: 3 }} key={achievement.label}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {achievement.number}
                  </Typography>
                  <Typography variant="h6">{achievement.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {achievement.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ maxWidth: 760, mx: 'auto', alignSelf: 'center' }}>
              <Stack spacing={4}>
                {[
                  {
                    title: 'The Breaking Point',
                    content: 'I remember the exact moment everything changed. Sitting in my car in the company parking lot, I did something I hadn\'t done since I was a kid: I cried. Not because something terrible happened, but because nothing was happening. My life was on autopilot, and I was just a passenger.',
                  },
                  {
                    title: 'The First Breakthrough',
                    content: 'I started small. Really small. A few hours on weekends. Some freelance experiments. I began learning about automation, remote work, location independence. Then something magical happened... My first breakthrough came when I built a simple AI automation system for local businesses. In month one, I made $10,000. Not $10,000 in revenue, $10,000 in profit. From my laptop. Working maybe 15 hours that month.',
                  },
                  {
                    title: 'Scaling Success',
                    content: 'But that was just the beginning. I landed my first $10,000 freelance client by positioning myself not as a desperate job-seeker, but as a strategic partner. I used storytelling and smart positioning to command premium prices. My photography and videography business? $50,000 in one year using the \'get paid to learn\' concept and strategic networking. I scaled a property management company from zero to over 200 units in 24 months by building systems and leveraging other people\'s time and money.',
                  },
                  {
                    title: 'Living the Dream',
                    content: 'Today, I\'ve lived and worked in over 20 countries. Not as a tourist rushing through a checklist, but as someone who could slow down, immerse myself in cultures, build relationships, and grow as a human being. Thailand, Portugal, Brazil, Kenya, Bali, Vietnam... Each place taught me something new, not just about the world, but about what\'s possible when you design life on your terms.',
                  },
                  {
                    title: 'Why This Matters to You',
                    content: 'I\'m not special. I don\'t have an MBA from Harvard. I wasn\'t born into wealth. I don\'t have some secret entrepreneurial gene. I was actually a terrible employee who couldn\'t fit the corporate mold. What I had was a system. A framework. A step-by-step process that anyone can follow. And that\'s exactly what I\'ve packaged into the Freedom Framework. This isn\'t theory. It\'s a battle-tested system that took me from corporate desperation to genuine freedom.',
                  },
                ].map((story, index) => (
                  <Box key={index} sx={{ pl: 3, py: 1, borderLeft: '3px solid', borderColor: 'secondary.main' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {story.title}
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {story.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>

          </Box>
        </Container>
      </Box>

      {/* Problem Section - Enhanced Awakening Awareness */}
      <Box
        sx={{
          bgcolor: 'background.default',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>

          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 800,
            }}
          >
            Look, You're Not Here By Accident...
          </Typography>

          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              maxWidth: 700,
              mx: 'auto',
              color: 'text.secondary',
              mb: 8,
              fontSize: '1.25rem',
              lineHeight: 1.6,
            }}
          >
            You can probably name the exact week you checked out. The reorg. The third "exceeds
            expectations" that came with a 3% raise. The Sunday you realised the dread started at
            4pm, not Monday morning.
          </Typography>

          <Stack spacing={6}>
            <Fade in timeout={800}>
              <Paper
                sx={{
                  p: { xs: 3, md: 6 },
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  borderRadius: 4,
                  border: '1px solid', borderColor: 'divider',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 800,
                    mb: 3,
                  }}
                >
                  You're not burned out.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 800,
                  }}
                >
                  You're <strong>comfortable</strong>, and quietly certain this is the whole plan.
                </Typography>
              </Paper>
            </Fade>

            <Fade in timeout={1200}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    textAlign: 'center',
                    mb: 4,
                    fontWeight: 700,
                    color: 'text.primary',
                  }}
                >
                  So here's the part nobody told you:
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    textAlign: 'center',
                    mb: 6,
                    fontWeight: 900,
                    lineHeight: 1.2,
                    color: 'primary.main',
                  }}
                >
                  the problem was never that you <br />
                  lacked the courage to quit.
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'primary.main',
                    maxWidth: 600,
                    mx: 'auto',
                    lineHeight: 1.7,
                  }}
                >
                  It's that nobody ever showed you the order of operations: what to build first,
                  what it has to be earning before you give notice, and what can wait until after.
                </Typography>

                {/* Call to Action Arrow */}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 80,
                      bgcolor: 'secondary.main',
                      mx: 'auto',
                      borderRadius: 2,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: `10px solid ${'#c87941'}`,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Stack>
        </Container>
      </Box>

      {/* Lands right after the problem section, where the reader has just been
          shown the trap and needs to see the way out. The subject sits in the
          right third, so the crop is anchored right to keep him in frame. */}
      <DreamBand
        image="/img/remote-work-mediterranean-terrace.jpg"
        alt="A man working remotely from a clifftop terrace on the Mediterranean coast, laptop and coffee on the table, sea and village behind him"
        position="70% center"
        headline="Somewhere it is a Tuesday afternoon, and nobody needs you in a meeting."
        sub="That is not a fantasy. It is a calendar, an income number, and about six months of unglamorous work."
      />

      {/* Course Introduction - Freedom Framework */}
      <Box id="framework" sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" gutterBottom>
            Here's How We Actually Make This Work
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            We've helped 500+ people just like you stop dreaming about freedom and start building it. This isn't theory. This is what actually moves the needle.
          </Typography>

          {/* Freedom Framework Visual */}
          <Paper
            sx={{
              p: { xs: 3, md: 6 },
              mb: 8,
              background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.05) 0%, rgba(200, 121, 65, 0.05) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 4,
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  border: '3px solid',
                  borderColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <Box sx={{ position: 'absolute', width: '100%', height: '2px', bgcolor: 'primary.main' }} />
                <Box sx={{ position: 'absolute', width: '2px', height: '100%', bgcolor: 'primary.main' }} />
                <Box sx={{ position: 'absolute', width: '70%', height: '2px', bgcolor: 'primary.main', transform: 'rotate(45deg)' }} />
                <Box sx={{ position: 'absolute', width: '70%', height: '2px', bgcolor: 'primary.main', transform: 'rotate(-45deg)' }} />
              </Box>

              <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                The Freedom Framework
              </Typography>

              <Typography variant="h6" textAlign="center" sx={{ maxWidth: 720 }}>
                It's three levers, pulled in this order.
              </Typography>

              <Stack spacing={3} sx={{ maxWidth: 760, width: '100%' }}>
                {[
                  {
                    n: '1',
                    name: 'ARBITRAGE',
                    body: "Keep earning at Western rates. Move your cost base to $600 to $1,800 a month. That gap does more for your runway than any raise you'll ever negotiate.",
                  },
                  {
                    n: '2',
                    name: 'OVERLAP',
                    body: "Take remote income to $2K, then $4K, then $6K a month while you're still on payroll, and past $8K once you've landed. You never resign into uncertainty. You resign into an already-replaced salary.",
                  },
                  {
                    n: '3',
                    name: 'LANDING',
                    body: 'Visa, banking, insurance, community and routine solved before you board, so month one abroad is boring instead of terrifying.',
                  },
                ].map((lever) => (
                  <Stack key={lever.n} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} alignItems="flex-start">
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2, minWidth: 32 }}
                    >
                      {lever.n}
                    </Typography>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: '0.5px' }}>
                        {lever.name}
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {lever.body}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Typography variant="body1" textAlign="center" sx={{ fontWeight: 600, maxWidth: 700 }}>
                The six modules exist to run those three levers. That's the whole framework.
              </Typography>
            </Stack>
          </Paper>

          {/* Benefits Grid */}
          <Grid container spacing={3}>
            {[
              { title: 'Stop dreaming, start planning', description: 'Build location freedom while you\'re still getting paid' },
              { title: 'Prosperity that energizes', description: 'Cut costs strategically without feeling deprived' },
              { title: 'Work that fits YOU', description: 'Meaningful pursuits that align with your passions and values' },
              { title: 'Live somewhere that matters', description: 'Not just cheap rent, but a place that actually feels like home' },
            ].map((benefit, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card sx={{ height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <CheckCircle color="primary" sx={{ fontSize: 32, mt: 0.5 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>{benefit.title}</Typography>
                        <Typography variant="body1" color="text.secondary">{benefit.description}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ mt: 6, p: 4, bgcolor: 'background.default', border: '2px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Typography variant="h6" textAlign="center" sx={{ }}>
              Because freedom shouldn't require you to become a different person. It should help you become more of who you already are.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Modules Section - Enhanced Value Proposition */}
      <Box
        id="curriculum"
        sx={{
          bgcolor: 'background.default',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          {/* Premium Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1.5 }}>
              SYSTEMATIC FREEDOM BLUEPRINT
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              Here's Exactly What You Get <br />
              <Box component="span" sx={{ color: 'primary.main' }}>
                Inside Each Module
              </Box>
            </Typography>

            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: 700, mx: 'auto', alignSelf: 'center' }}>
              Every module delivers specific, actionable frameworks that have transformed 500+ people from corporate prisoners to genuine freedom achievers.
            </Typography>
          </Box>

          {/* Enhanced Module Cards */}
          <Grid container spacing={4}>
            {[
              {
                number: '01',
                title: 'The Wake-Up Call',
                fullTitle: 'Module 1: Consciousness Shift + Limiting Belief Detox',
                duration: 'Week 1',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  'Belief System Audit ($197 value)',
                  'Mindset Transformation Blueprint',
                  'Freedom Vision Canvas Exercise',
                  'Personal Values Alignment Tool',
                  'Limiting Belief Burning Ceremony'
                ],
                outcome: 'Clarity on what you actually want, usually inside a fortnight',
                students: 'Students describe it as the first time leaving felt real'
              },
              {
                number: '02',
                title: 'Financial Freedom Formula',
                fullTitle: 'Module 2: Geographic Arbitrage + Income Optimization',
                duration: 'Week 2',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  'Cost of Living Analysis Calculator',
                  'Geographic Income Multiply Tool',
                  'Personal Budget Freedom Framework',
                  '10+ Lower-Cost Country Research Database',
                  'Financial Freedom Timeline Calculator'
                ],
                outcome: 'Average $2,400/month spending reduction',
                students: 'A spending plan you can live on, not a crash diet'
              },
              {
                number: '03',
                title: 'Remote Income Engine',
                fullTitle: 'Module 3: Skill-Based + Passive Income Systems',
                duration: 'Week 3',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  'Laptop Lifestyle Income Canvas',
                  'Personal Brand & Client Acquisition System',
                  '15+ Passive Income Stream Templates',
                  'Premium Pricing Psychology Framework',
                  'Quick-Win Freelance Opportunity Matrix'
                ],
                outcome: '$8K-15K monthly income templates',
                students: 'Your first paid client, using the outreach templates included'
              },
              {
                number: '04',
                title: 'Location Intelligence',
                fullTitle: 'Module 4: Country Research + Visa Strategy Masterclass',
                duration: 'Week 4',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  '50+ Country Deep-Dive Database',
                  'Visa Success Rate Tracking Tool',
                  'Language Learning Fast-Track System',
                  'Local Community Integration Guide',
                  'Nomad to Local Transition Framework'
                ],
                outcome: 'Visa pathways mapped for 50+ countries, plus when to bring in a specialist',
                students: 'A shortlist of countries that fit your budget, visa and life'
              },
              {
                number: '05',
                title: 'Exit Execution Plan',
                fullTitle: 'Module 5: Corporate Escape + Risk Management Blueprint',
                duration: 'Week 5',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  '6-Month Exit Timeline Template',
                  'Risk Assessment & Mitigation Matrix',
                  'Employer Negotiation Scripts',
                  'Financial Runway Calculator',
                  'Pre-Exit Income Experimentation Guide'
                ],
                outcome: 'A resignation that leaves the bridge intact',
                students: 'You finish this module holding a dated exit plan'
              },
              {
                number: '06',
                title: 'Thrive as a Free Human',
                fullTitle: 'Module 6: Long-Term Freedom Lifestyle + Community Building',
                duration: 'Week 6 + ongoing',
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                features: [
                  'Freedom Routine Optimization System',
                  'Global Community Integration Roadmap',
                  'Digital Nomad Mental Health Toolkit',
                  'Purpose & Meaning Discovery Framework',
                  'Long-Term Freedom Lifestyle Guide'
                ],
                outcome: 'A life abroad that still works in month twelve, not just month one',
                students: 'A community that stops the first months feeling lonely'
              },
            ].map((module, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={module.number}>
                <Fade in timeout={600 + (index * 150)}>
                  <Card
                    sx={{
                      height: '100%',
                      position: 'relative',
                      background: `linear-gradient(135deg, ${module.bgColor} 0%, rgba(255, 255, 255, 0.98) 100%)`,
                      border: `3px solid ${module.borderColor}`,
                      borderRadius: 4,
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        '& .module-icon': {
                          transform: 'scale(1.15) rotate(5deg)',
                        },
                        '& .module-number': {
                          transform: 'scale(1.1)',
                        },
                        '& .feature-list': {
                          opacity: 1,
                          transform: 'translateX(0)',
                        },
                      },
                    }}
                  >
                    {/* Module Number Badge */}
                    <Box
                      className="module-number"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: module.color,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        fontWeight: 800,
                        transition: 'all 0.3s ease',
                        zIndex: 2,
                      }}
                    >
                      {module.number}
                    </Box>

                    {/* Duration Badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        background: 'rgba(255, 255, 255, 0.95)',
                        color: module.color,
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: `1px solid ${module.borderColor}`,
                      }}
                    >
                      {module.duration}
                    </Box>

                    <CardContent sx={{ p: 4, pt: 5 }}>
                      <Stack spacing={3}>

                        {/* Title Section */}
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
                            {module.title}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: module.color }}>
                            {module.fullTitle}
                          </Typography>
                        </Box>

                        {/* Feature List */}
                        <Box className="feature-list" sx={{ mt: 2, opacity: 0.8, transform: 'translateX(-10px)', transition: 'all 0.3s ease' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary', textAlign: 'center' }}>
                            What You Get Inside:
                          </Typography>
                          <Stack spacing={1}>
                            {module.features.map((feature, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.85rem',
                                  lineHeight: 1.5,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  '&::before': {
                                    content: '"•"',
                                    color: module.color,
                                    mr: 1.5,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  },
                                }}
                              >
                                {feature}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>

                        {/* Results Section */}
                        <Box sx={{ mt: 2, p: 2, bgcolor: module.bgColor.replace('0.08', '0.15'), borderRadius: 2, border: `1px solid ${module.borderColor.replace('0.4', '0.2')}` }}>
                          <Typography variant="caption" sx={{ color: module.color, fontWeight: 700, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            REAL RESULTS:
                          </Typography>

                          <Stack spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem' }}>
                              {module.outcome}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {module.students}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>

                    {/* Enhanced hover border */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${module.color}, rgba(255, 255, 255, 0.3), ${module.color})`,
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 3s ease-in-out infinite',
                      }}
                    />
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* Closing proof. Two nested Papers on the same surface as the panel
              behind them had no edges, so the stats read as text floating in
              whitespace. Flat stat blocks in a grid instead. */}
          <Box
            sx={{
              mt: 8,
              p: { xs: 3, md: 5 },
              textAlign: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>
              Nothing here is theory
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto', alignSelf: 'center' }}>
              Every tool, template and framework has been tested with real students. You get the
              files, not the summary.
            </Typography>

          </Box>
        </Container>
      </Box>

      {/* Course Details Section - Interactive Timeline Design */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 30%, rgba(30, 58, 95, 0.02) 0%, transparent 70%), radial-gradient(circle at 80% 70%, rgba(200, 121, 65, 0.03) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          {/* Dynamic Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1.5 }}>
              THE JOURNEY BEGINS
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              Your Freedom Journey Starts{" "}
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                }}
              >
                Soon
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                color: 'text.secondary',
                mb: 4,
              }}
            >
              Ten people per cohort, because I read every exit plan personally and give feedback on each one.
            </Typography>

            {/* Urgency Stats */}
            <Grid container spacing={3} justifyContent="center" sx={{ mb: 6 }}>
              <Grid size={{ xs: 4, sm: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    10
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SPOTS
                  </Typography>
                </Box>
              </Grid>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Grid size={{ xs: 4, sm: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    6
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    WEEKS
                  </Typography>
                </Box>
              </Grid>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Grid size={{ xs: 4, sm: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                    Lifetime
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Access
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Premium Timeline Cards */}
          <Grid container spacing={4}>
            {[
              {
                id: 'start-date',
                phase: 'PHASE 1',
                title: 'Launch Day',
                date: 'September 1, 2026',
                day: 'Tuesday',
                time: '9:00 AM PST',
                description: 'Your transformation begins. Join the elite group of visionaries committed to freedom.',
                icon: <CalendarToday sx={{ fontSize: 48 }} />,
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                status: 'CONFIRMED',
                highlight: true,
              },
              {
                id: 'duration',
                phase: 'PHASE 2-7',
                title: 'Deep Dive',
                date: '6 Comprehensive Modules',
                day: 'Weekly Mastery',
                time: 'Every Sunday',
                description: '6 breakthrough modules designed by someone who\'s lived the transformation you seek.',
                icon: <School sx={{ fontSize: 48 }} />,
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                status: 'MASTERFUL',
                modules: [
                  'The Wake-Up Call',
                  'Financial Freedom',
                  'Remote Income Mastery',
                  'Location Intelligence',
                  'Exit Strategy',
                  'Thriving Free'
                ],
              },
              {
                id: 'spots',
                phase: 'ELITE ACCESS',
                title: 'Exclusive Circle',
                date: 'Only 10 Visionaries',
                day: 'Personalized Journey',
                time: 'Weekly group calls',
                description: 'Why 10? Because real transformation requires deep intimacy. Each person gets my personal attention.',
                icon: <Group sx={{ fontSize: 48 }} />,
                color: 'primary.main',
                bgColor: 'background.paper',
                borderColor: 'divider',
                status: 'LIMITED',
              },
            ].map((item, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.id}>
                <Fade in timeout={800 + (index * 300)}>
                  <Card
                    sx={{
                      height: '100%',
                      position: 'relative',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      // One card carried a 3px green border and a pulsing green
                      // glow while its siblings were plain, which read as a
                      // rendering fault rather than emphasis.
                      borderColor: item.highlight ? 'primary.main' : 'divider',
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Status Badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: item.color,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        zIndex: 2,
                      }}
                    >
                      {item.status}
                    </Box>

                    <CardContent sx={{ p: 4, pb: item.modules ? 6 : 4 }}>
                      {/* Phase Indicator */}
                      <Typography
                        variant="overline"
                        sx={{
                          color: item.color,
                          fontWeight: 700,
                          letterSpacing: '1px',
                          display: 'block',
                          mb: 2,
                          textAlign: 'center',
                        }}
                      >
                        {item.phase}
                      </Typography>

                      {/* Animated Icon */}
                      <Box
                        className="timeline-icon"
                        sx={{
                          color: item.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          transition: 'all 0.3s ease',
                          borderRadius: '50%',
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          background: `rgba(${item.color === 'success.main' ? '16, 185, 129' :
                            item.color === 'primary.main' ? '30, 58, 95' :
                            '200, 121, 65'}, 0.1)`,
                        }}
                      >
                        {item.icon}
                      </Box>

                      {/* Content */}
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: 'text.primary',
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography variant="h4" sx={{ color: item.color, fontWeight: 800, mb: 1 }}>
                          {item.date}
                        </Typography>

                        <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                          {item.day}
                        </Typography>

                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                          {item.time}
                        </Typography>

                        <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                          {item.description}
                        </Typography>

                        {/* Module List for Duration Phase */}
                        {item.modules && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                              MODULE BREAKDOWN:
                            </Typography>
                            <Stack spacing={0.5}>
                              {item.modules.map((module, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.7rem',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:before': {
                                      content: '""',
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      bgcolor: item.color,
                                      mr: 1,
                                      flexShrink: 0,
                                    },
                                  }}
                                >
                                  {module}
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </CardContent>

                    {/* Decorative Bottom Border */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${item.color}, rgba(255, 255, 255, 0.3))`,
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 3s ease-in-out infinite',
                      }}
                    />
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {/* Call to Action */}
          <Paper
            sx={{
              mt: 8,
              p: { xs: 3, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.05), rgba(200, 121, 65, 0.03))',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.3,
                pointerEvents: 'none',
              }}
            />

            <Stack spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Ready to Put a Date on It?
              </Typography>
              <Typography variant="h6" sx={{ maxWidth: 560, mx: 'auto', alignSelf: 'center', color: 'text.secondary' }}>
                The {COHORT_START} cohort takes 10 people. The call is where we find out whether
                you're one of them, and what your first 90 days would actually look like.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={scrollToEnroll}
                  sx={{ mt: 2, fontWeight: 700 }}
                >
                  {CTA_LABEL} →
                </Button>
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                  {CTA_SUBLINE}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Container>

      </Box>

      {/* What's Included Section */}
      <Box sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" textAlign="center" gutterBottom>
            What's Included
          </Typography>

          <List sx={{ mt: 4 }}>
            {[
              'Full access to all 6 core modules + bonus mindset module',
              'Weekly live group coaching calls (recorded if you can\'t attend live)',
              'Downloadable worksheets: Vision Map, Freedom Fund Tracker, Country Shortlist Template, Exit Timeline Planner',
              'Private community for accountability and connection',
              'Lifetime access to course materials',
            ].map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  bgcolor: 'background.default',
                  mb: 2,
                  borderRadius: 2,
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 1,
                }}
              >
                <ListItemIcon>
                  <CheckCircle color="primary" sx={{ fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontSize: '1.125rem', fontWeight: 500 }}>{item}</Typography>}
                />
              </ListItem>
            ))}
          </List>

          <Paper
            sx={{
              mt: 6,
              p: { xs: 2.5, md: 4 },
              textAlign: 'center',
              bgcolor: 'background.default',
              border: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h4" color="primary.main" gutterBottom sx={{ fontWeight: 700 }}>
              Proven Results That Transform Lives
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.125rem' }}>
              Join thousands who've discovered their path to genuine freedom. Our students consistently report feeling empowered, focused, and excited about their future. This isn't just another course. It's a life-changing experience that delivers real, lasting transformation.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 12 } }}>
        <Container>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 6 }}>
            <Typography variant="h2">Real Talk From Past Students</Typography>
          </Stack>

          <Grid container spacing={4}>
            {[
              {
                name: 'Sarah M., 34',
                role: 'Former Tech Manager',
                image: '/img/Sarah%20M.png',
                text: '"I was searching for something more meaningful, wondering if a different way of life was possible for me. Six months later, I\'m living my dream from a beautiful town in Bali where my lifestyle costs half what it did in Europe. This course didn\'t just inspire me. It gave me a complete transformation."',
              },
              {
                name: 'Marcus R., 31',
                role: 'CPA & Online Business Owner',
                image: '/img/Marcus%20R.png',
                text: '"I explored many paths but couldn\'t find the right fit until I discovered this comprehensive approach. This course met me exactly where I was, an accountant who wanted to work remotely and live somewhere warm. Authentic guidance, real results. I\'m in Mexico City now, paying $800 for a beautiful place that would cost $2,500 in LA."',
              },
            ].map((testimonial, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack spacing={3}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          src={testimonial.image}
                          alt={testimonial.name}
                          sx={{ width: 80, height: 80, border: '3px solid', borderColor: 'secondary.main' }}
                        />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8, fontStyle: 'italic' }}>
                        {testimonial.text}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} sx={{ color: 'primary.main' }} />
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* For You If Section - Enhanced Target Audience Resonance */}
      <Box
        sx={{
          bgcolor: 'background.default',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          {/* Powerful Headline Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1.5 }}>
              PERFECT STUDENT PROFILE
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              This Course Is For YOU If...
            </Typography>

            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: 700, mx: 'auto', alignSelf: 'center' }}>
              You're finally ready to stop dreaming about freedom and start building it systematically. Let's see if you're the perfect fit for this transformation.
            </Typography>
          </Box>

          {/* Enhanced Yes List - What You Get */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h4" sx={{ mb: 4, color: 'primary.main', fontWeight: 800, textAlign: 'center' }}>
              YES! This Is Perfect For You If You:
            </Typography>

            <Grid container spacing={3}>
              {[
                {
                  text: 'Wake up every morning excited about your future, not dreading going to work',
                  impact: 'Students report this inside the first month',
                  color: 'primary.main',
                  weight: 'bold'
                },
                {
                  text: 'Want to live where you want, work when you want, and still maintain (or grow) your income',
                  impact: '$8K-15K average monthly income our students achieve',
                  color: 'primary.main',
                  weight: 'bold'
                },
                {
                  text: 'Are tired of "digital nomad" fantasies and want real systems that actually work',
                  impact: 'Built to be finished, one lever at a time, no overwhelm',
                  color: 'primary.main',
                  weight: 'bold'
                },
                {
                  text: 'Want genuine relationships with like-minded people who GET your journey',
                  impact: '500+ members in our private accountability community',
                  color: 'primary.main',
                  weight: 'bold'
                },
                {
                  text: 'Have tried other courses but nothing stuck - you need frameworks that WORK',
                  impact: 'The word students reach for most often is "practical"',
                  color: 'primary.main',
                  weight: 'bold'
                },
              ].map((item, index) => (
                <Grid size={{ xs: 12, md: 6 }} key={`yes-${index}`}>
                  <Fade in timeout={700 + (index * 200)}>
                    <Paper
                      elevation={item.weight === 'bold' ? 6 : 3}
                      sx={{
                        // Sides and bottom only: mixing the `p` shorthand with
                        // `pt` lets MUI emit `padding` after `padding-top`, which
                        // silently discards the top value.
                        px: { xs: 2.5, md: 4 },
                        pb: { xs: 2.5, md: 4 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        // Clears the badge, which is absolutely positioned at the
                        // top left and was sitting on top of the first word.
                        pt: 9,
                      }}
                    >
                      {/* Checkmark Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 20,
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 28 }} />
                      </Box>

                      {/* Main Text */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 2,
                          lineHeight: 1.5,
                          textAlign: 'center',
                        }}
                      >
                        {item.text}
                      </Typography>

                      {/* Impact Metric */}
                      <Box sx={{ mt: 'auto' }}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: 'background.default',
                            borderRadius: 2,
                            border: `1px solid ${item.color}`
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: item.color,
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              mb: 0.5,
                              display: 'block',
                            }}
                          >
                            REAL IMPACT IN OUR COMMUNITY:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '0.85rem',
                            }}
                          >
                            {item.impact}
                          </Typography>
                        </Paper>
                      </Box>
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>



          {/* Perfect Fit Confirmation */}
          <Paper
            sx={{
              p: { xs: 3, md: 6 },
              textAlign: 'center',
              bgcolor: 'background.default',
              border: '3px solid',
              borderColor: 'divider',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Animated background */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.5,
                animation: 'float 6s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            />

            <Stack spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                Sounds Like You?
              </Typography>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                You're not just another person dreaming about freedom...
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, lineHeight: 1.6, maxWidth: 700, mx: 'auto', alignSelf: 'center' }}>
                You're someone who's ready for systematic transformation. Ready for real tools. Ready for genuine change. Ready to become one of our success stories. And that? That's exactly why you're reading this right now.
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="center">
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                  Welcome to Your Freedom Journey! </Typography>

                <Box sx={{ width: 4, height: 4, bgcolor: 'primary.main', borderRadius: '50%' }} />

                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  500+ People Like You Are Already Free
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* The disqualification half. Every premium reference has one, and it
              is the move a scam funnel structurally cannot make. */}
          <Box sx={{ mt: 10, maxWidth: 760, mx: 'auto', alignSelf: 'center' }}>
            {/* Heading block centred to match every other section; the list below
                stays left aligned, since centred list items are unscannable. */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                And who it is not for
              </Typography>
              <Typography variant="h3" gutterBottom>
                Please don't book a call if any of these are true
              </Typography>
              <Typography variant="body1" color="text.secondary">
                I would rather tell you now than take your money and watch it not work.
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              {[
                {
                  no: 'You want income without building anything.',
                  why: 'Months 1 to 3 are unglamorous: choosing a skill, making a portfolio, sending outreach that gets ignored. There is no version of this where money arrives before the work does.',
                },
                {
                  no: 'You need to be earning within 30 days.',
                  why: 'The plan replaces a salary over roughly six months while you keep the salary. If your runway is measured in weeks, fix that first. A strategy call will not change your arithmetic.',
                },
                {
                  no: 'You want someone to tell you exactly what to do, forever.',
                  why: 'You get frameworks, templates and weekly group calls. You do not get someone who makes your decisions. The people this works for decide, then ask for a check on the decision.',
                },
                {
                  no: 'You are not willing to be a beginner in public.',
                  why: 'Your first client work will be worse than your corporate work, and it will be priced lower. Everyone who gets through month three ate that.',
                },
                {
                  no: 'You are looking for permission rather than a plan.',
                  why: 'If what you actually want is for someone to say your idea is good, a friend can do that for free and it will feel better than this will.',
                },
              ].map((item, index) => (
                <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                  <Cancel sx={{ color: 'grey.400', mt: 0.4, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="h5" sx={{ mb: 0.5 }}>
                      {item.no}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {item.why}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>

            <Typography variant="body1" sx={{ mt: 4, fontWeight: 600 }}>
              If none of that put you off, the call is worth having. I'd rather lose 30 minutes
              than take money from someone who isn't ready.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Community Learning Section - People-Centric Transformation */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          {/* Emotional Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1.5 }}>
              HUMAN CONNECTION THAT CHANGES EVERYTHING
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              People Like You. <br />
              <Box
                component="span"
                sx={{
                  color: 'primary.main',
                }}
              >
                Making It Real.
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{
                maxWidth: 650,
                mx: 'auto',
                color: 'text.secondary',
                mb: 4,
              }}
            >
              Transform solo struggle into shared victory. Your breakthrough becomes everyone else's success story.
            </Typography>

            {/* Additional CTA Button in Community Section */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  py: 2,
                  px: 4,
                  borderColor: 'divider',
                  color: 'primary.main',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: 'divider',
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => setShowRoadmap(true)}
              >
                Get My Free Corporate Exit Roadmap
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Join 500+ members who've transformed their lives
              </Typography>
            </Box>

            {/* Community Stats with Faces */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 3, md: 6 }}
              justifyContent="center"
              sx={{ mb: 4 }}
              alignItems="center"
            >
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', mb: 1, justifyContent: 'center' }}>
                  {MEMBER_AVATARS.map((id, i) => (
                    <Avatar
                      key={id}
                      src={`https://images.unsplash.com/${id}?w=64&h=64&fit=crop&crop=faces&auto=format&q=80`}
                      alt=""
                      sx={{
                        width: 24,
                        height: 24,
                        border: '2px solid white',
                        ml: i > 0 ? -1 : 0,
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  500+ Members
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 800, mb: 1 }}>
                  6 weeks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Structured curriculum
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 800, mb: 1 }}>
                  Lifetime
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Access to materials
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Corporate Escape Calculator Lead Magnet */}
      <Box
        sx={{
          // bgcolor maps to background-color, which cannot take a gradient, so
          // this section rendered with no background at all.
          background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.05), rgba(200, 121, 65, 0.03))',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.5,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', display: 'block', mb: 1.5 }}>
              NOT READY FOR A CALL YET? START HERE
            </Typography>

            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              The Corporate Escape Calculator
            </Typography>

            <Typography variant="h4" sx={{ mb: 6, fontWeight: 600, color: 'primary.main' }}>
              FREE Interactive Tool
            </Typography>
          </Box>

          <Stack spacing={4} alignItems="center">
            <Paper
              sx={{
                p: { xs: 3, md: 6 },
                borderRadius: 4,
                bgcolor: 'background.default',
                border: '3px solid',
                borderColor: 'primary.main',
                maxWidth: 600,
              }}
            >
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: 'primary.main', textAlign: 'center' }}>
                Your Information:
              </Typography>
              <Stack spacing={4}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Annual Salary
                  </Typography>
                  <input
                    type="number"
                    placeholder="e.g. 120000"
                    value={calculatorInput.salary}
                    onChange={(e) => setCalculatorInput({...calculatorInput, salary: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e1e5e9',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      marginBottom: '4px'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Your current annual income from your job
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Total Savings
                  </Typography>
                  <input
                    type="number"
                    placeholder="e.g. 50000"
                    value={calculatorInput.savings}
                    onChange={(e) => setCalculatorInput({...calculatorInput, savings: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e1e5e9',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      marginBottom: '4px'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Emergency fund + investable assets
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Target Location
                  </Typography>
                  <select
                    value={calculatorInput.location}
                    onChange={(e) => setCalculatorInput({...calculatorInput, location: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e1e5e9',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      background: 'white',
                      marginBottom: '4px'
                    }}
                  >
                    {Object.keys(costOfLivingData).map(location => (
                      <option key={location} value={location}>
                        {location} (${costOfLivingData[location]}/month living)
                      </option>
                    ))}
                  </select>
                  <Typography variant="caption" color="text.secondary">
                    Average cost of living for comfortable lifestyle
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    const results = calculateResults();
                    setCalculationResults(results);
                  }}
                  sx={{
                    mt: 2,
                    py: 2,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Calculate My Freedom Timeline
                </Button>
              </Stack>
            </Paper>

            {calculationResults && (
              <Paper
                sx={{
                  p: { xs: 3, md: 6 },
                  borderRadius: 4,
                  bgcolor: 'background.default',
                  border: '3px solid',
                  borderColor: 'divider',
                  maxWidth: 600,
                }}
              >
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: 'primary.main', textAlign: 'center' }}>
                  Your Results:
                </Typography>
                <Stack spacing={4}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Your Earliest Safe Exit
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 800, my: 1 }}>
                      {calculationResults ? calculationResults.exitDate : 'Enter your details above'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {/* exitDate is not always a date: it can be a prompt or a verdict,
                          so the caption must not promise one. */}
                      {calculationResults && /\d{4}$/.test(calculationResults.exitDate)
                        ? 'The earliest you could quit without burning your runway'
                        : 'Based on six months of runway in your target country'}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                      Financial Runway Needed
                    </Typography>
                    <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 800, my: 1 }}>
                      {calculationResults ? calculationResults.monthlyRunwayNeeded : 'Enter your details above'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Savings required for 6 months of freedom
                    </Typography>
                  </Box>

                  {calculationResults && (
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 215, 0, 0.05))',
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                        Your Freedom Timeline
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                        {calculationResults.timeframe}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                        Timeframe to reach financial freedom
                      </Typography>
                    </Paper>
                  )}
                </Stack>

                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderColor: 'divider',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'divider',
                      bgcolor: 'primary.main',
                      color: 'white',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => setShowRoadmap(true)}
                >
                  Get My Free Corporate Exit Roadmap
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    mt: 2,
                    py: 2,
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => document.getElementById('enroll')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Turn This Date Into a Plan
                  <br />
                  {CTA_LABEL} →
                </Button>

                <Typography variant="body2" sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.secondary', maxWidth: 620, mx: 'auto', alignSelf: 'center' }}>
                  That date assumes nothing changes. On the call we stress-test it against your real
                  expenses, your visa options and the skills you already get paid for, then build the
                  6-month sequence that pulls the date forward. {CTA_SUBLINE}
                </Typography>
              </Paper>
            )}
          </Stack>

          {/* Urgency Element */}
          <Paper
            sx={{
              mt: 8,
              p: { xs: 2.5, md: 4 },
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 4,
              border: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
              Two Honest Numbers, in Under a Minute
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Most people spend years guessing when they can quit. This gives you the runway you
              actually need, priced in your target country rather than your current one, and how long
              saving alone would take to get there. If that number is years away, that's the whole
              reason the income half of this exists.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Objections. The page previously answered aspiration for 2,900 lines and never
          touched the logistics questions that actually stop people booking. */}
      <Box id="faq" sx={{ bgcolor: 'background.default', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" textAlign="center" gutterBottom>
            The Questions You're Actually Asking at 11pm
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Not the ones that make the course look good. The ones that keep you where you are.
          </Typography>

          <Stack spacing={2}>
            {[
              {
                q: 'What happens to my health insurance?',
                a: "You don't go uninsured for a day. Before you resign we price your options side by side: continuation cover from your current employer, an international plan (SafetyWing, Genki, Cigna Global), or in-country private cover, which in most of the countries on our list costs a fraction of a US premium. We do this in Module 5, before you give notice, not after.",
              },
              {
                q: 'How much runway do I actually need?',
                a: "Six months of your TARGET country's costs, not your current ones. That's the whole point. The calculator above prices Vietnam at $4,200 and Portugal at $10,800 for six months. Most people have been carrying a number in their head that's three times too high. And you don't touch that runway until remote income already covers your bills.",
              },
              {
                q: "I don't have a skill anyone would pay me for.",
                a: "Almost nobody arrives thinking they have one. What corporate employees do have is process, project management, reporting, vendor handling, and spreadsheets other people find terrifying. Module 3 is about naming what you already do and finding who pays for it. If we get on the call and I genuinely can't see something saleable, I'll tell you, and I won't sell you the course.",
              },
              {
                q: 'Visas and taxes: is any of this actually legal?',
                a: "Yes, and it's duller than you'd think. Most students start on tourist or digital-nomad visas in countries that publish clear rules, and stay tax-resident at home for the first year. Module 4 maps the pathways for 50+ countries and tells you exactly when your situation is complex enough to bring in a specialist. I'm not an immigration adviser and I won't pretend to be.",
              },
              {
                q: 'My partner thinks this is insane.',
                a: "Then we don't work around them. Bring them to the call. Two people on the line is normal here. What changes a partner's mind is rarely a speech; it's seeing the month-by-month plan where nobody quits anything until the income is already there.",
              },
              {
                q: "What if it doesn't work and I want to come back?",
                a: "Then you come back, with 6 to 12 months of freelance or contract work on your CV, which hiring managers read as initiative, not instability. You'd be returning to the same job market with more skills than you left with. Leaving is not a door that locks behind you.",
              },
              {
                q: "How do I know you're not just another guru?",
                a: "You don't, yet. And you shouldn't take my word for it from a landing page. That's what the free call is for. You'll talk to me, you'll get a plan you can execute whether or not you ever buy anything, and I'd rather lose 30 minutes than take money from someone who isn't ready.",
              },
            ].map((item, index) => (
              <Accordion
                key={index}
                disableGutters
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  boxShadow: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.075rem' }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ lineHeight: 1.85, color: 'text.secondary' }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={scrollToEnroll}
              sx={{ fontWeight: 700 }}
            >
              {CTA_LABEL} →
            </Button>
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              {CTA_SUBLINE}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Last emotional beat before the booking widget. Deliberately rhymes with
          the hero: same coast road, same golden hour, so the page opens and
          closes on the same image of freedom. */}
      <DreamBand
        image="/img/open-road-location-freedom-sunset.jpg"
        alt="A traveller standing beside a parked car on an open coastal road at sunset, mountains and ocean stretching ahead"
        position="center 55%"
        headline="Do what you want, when you want, from wherever you want."
        sub="The whole point of the six months is that the seventh looks like this."
      />

      {/* Final CTA */}
      <Box
        id="enroll"
        sx={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f1f3d 100%)',
          color: 'white',
          py: { xs: 6, md: 12 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom>
            Look, You've Read This Far For a Reason
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            You're not here because you want to "become a digital nomad." You're here because you're tired of feeling trapped.
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, maxWidth: 720, mx: 'auto', alignSelf: 'center', textAlign: 'left' }}>
            So here's exactly what the next 30 minutes are. We put your real numbers on the table:
            salary, savings, notice period, and what you already do that someone would pay for. You
            leave with a target country, a realistic exit month, and the first income stream to
            build, yours whether or not you ever pay me a cent. At the end I'll tell you straight
            whether the {COHORT_START} cohort is a fit, and what it costs. If it isn't a fit, I'll
            say so and we're done early.
          </Typography>

          <BookingWidget />

          <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
            {CTA_SUBLINE}
          </Typography>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 2, display: 'inline-block', maxWidth: '100%' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'secondary.main', mb: 1 }}>
              Join the private community of corporate escapees
            </Typography>
            <CountdownTimer targetDate={COHORT_START_TS} compact={true} />
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#171717', color: 'white', py: 6, textAlign: 'center' }}>
        <Container>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{ mb: 3, cursor: 'pointer' }}
          >
            <Avatar
              src="/img/the-freedome-framework-logo.png"
              alt="The Freedom Framework"
              sx={{ width: 48, height: 48 }}
            />
            <Box textAlign="left">
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                The Freedom Framework
              </Typography>
              <Typography variant="caption" sx={{ color: 'secondary.main' }}>
                Build Your Perfect Life
              </Typography>
            </Box>
          </Stack>
          <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 700, mx: 'auto', alignSelf: 'center' }}>
            P.S. You don't need to be "special" or "brave" or "entrepreneurial" to build a life you love. You just need to be open for more possibilities. If that's you, let's talk.
          </Typography>

          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.15)' }} />

          <Typography variant="caption" sx={{ display: 'block', opacity: 0.6, maxWidth: 820, mx: 'auto', alignSelf: 'center', lineHeight: 1.7 }}>
            Earnings disclosure: any income figures on this page describe specific individuals'
            results and are not typical, guaranteed or promised. Most people who buy business
            courses do not replace their salary. Your results depend on your skills, your market,
            your effort and your circumstances. This is education, not a business opportunity, an
            investment, or immigration or tax advice.
          </Typography>
        </Container>
      </Box>

      {/* Persistent booking CTA. This used to be a scroll-to-top button: the one
          always-visible control on a 2,900-line sales page, pointing away from the offer. */}
      {showScrollTop && !enrollInView && (
        <Fab
          variant="extended"
          color="secondary"
          size="large"
          onClick={scrollToEnroll}
          sx={{
            // Hidden below md: the sticky header already carries a booking
            // button there, and this one covered the copy.
            display: { xs: 'none', md: 'inline-flex' },
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            px: 3,
            fontWeight: 700,
            '&:hover': {
              transform: 'scale(1.05)',
            },
            transition: 'all 0.3s ease',
          }}
          aria-label="Book your free 30-minute strategy call"
        >
          <CalendarToday sx={{ mr: 1 }} />
          Book Free Call, 30 min
        </Fab>
      )}
    </Box>
  );
}

export default App;
